"use client";

import { migrateAppKey, LEGACY_VCARDS_KEYS, VCARDS_KEY } from "@/lib/vcards/vcardsApp";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabaseClient";

/** Refs that name the same app: vCards also matches rows saved as page:editor / page:entities. */
function refAliases(_kind: string, ref: string): string[] {
  const canonical = migrateAppKey(ref);
  const prefix = canonical.startsWith("page:") ? "page:" : "";
  const bare = canonical.slice(prefix.length);
  if (bare !== VCARDS_KEY) return [ref];
  return [VCARDS_KEY, ...LEGACY_VCARDS_KEYS].map((k) => `${prefix}${k}`);
}

export type FolderItemKind = "app" | "nav";

export interface FolderMember {
  kind: FolderItemKind;
  ref: string;
  sort_order: number;
  created_at: string;
}

export interface AppFolder {
  id: string;
  name: string;
  icon: string | null;
  color: string | null;
  sort_order: number;
  members: FolderMember[];
}

interface FolderRow {
  id: string;
  name: string;
  icon: string | null;
  color: string | null;
  sort_order: number;
}

interface MembershipRow {
  folder_id: string;
  item_kind: FolderItemKind;
  item_ref: string;
  sort_order: number;
  created_at: string;
}

const QUERY_KEY = ["app-folders"] as const;

async function fetchFolders(): Promise<AppFolder[]> {
  const { data: folders, error: fErr } = await supabase
    .from("user_app_folders")
    .select("id, name, icon, color, sort_order")
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: true });
  if (fErr) throw fErr;

  const folderRows = (folders ?? []) as FolderRow[];
  if (folderRows.length === 0) return [];

  const { data: members, error: mErr } = await supabase
    .from("user_app_folder_memberships")
    .select("folder_id, item_kind, item_ref, sort_order, created_at")
    .in("folder_id", folderRows.map((f) => f.id));
  if (mErr) throw mErr;

  const byFolder = new Map<string, FolderMember[]>();
  for (const m of (members ?? []) as MembershipRow[]) {
    const list = byFolder.get(m.folder_id) ?? [];
    // Editor/Entities → vCards: old rows keep working, and a folder holding both shows one tile.
    const ref = migrateAppKey(m.item_ref);
    if (list.some((x) => x.kind === m.item_kind && x.ref === ref)) continue;
    list.push({ kind: m.item_kind, ref, sort_order: m.sort_order, created_at: m.created_at });
    byFolder.set(m.folder_id, list);
  }

  return folderRows.map((f) => ({
    ...f,
    members: byFolder.get(f.id) ?? [],
  }));
}

export function useAppFolders() {
  const { data, isLoading, error } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: fetchFolders,
    staleTime: 60 * 1000,
  });
  return { folders: data ?? [], isLoading, error: error as Error | null };
}

export function useAppFolderMutations() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: QUERY_KEY });

  // Default icons rotated through when user creates a folder without
  // specifying one. Picked so every new folder has a distinct visual,
  // avoiding the "all FolderOpen" wall (Sprint C.3, 2026-05-15).
  const DEFAULT_FOLDER_ICONS = [
    "Folder", "Briefcase", "Star", "Bookmark", "Tag", "Archive",
    "Package", "Box", "Gift", "Heart", "Flag", "Layers", "Grid3X3",
    "Compass", "Sparkles", "Zap", "Rocket", "Target", "Trophy", "Crown",
  ];

  const createFolder = useMutation({
    mutationFn: async (input: { name: string; icon?: string | null; color?: string | null }) => {
      const trimmed = input.name.trim();
      if (!trimmed) throw new Error("Folder name required");
      const { data: existing } = await supabase
        .from("user_app_folders")
        .select("sort_order")
        .order("sort_order", { ascending: false })
        .limit(1);
      const nextOrder = ((existing?.[0]?.sort_order as number | undefined) ?? -1) + 1;
      // Auto-assign a default icon if caller didn't choose one. Rotates
      // through DEFAULT_FOLDER_ICONS so each new folder differs visually.
      const autoIcon = DEFAULT_FOLDER_ICONS[nextOrder % DEFAULT_FOLDER_ICONS.length];
      const finalIcon = input.icon ?? autoIcon;
      const { data, error } = await supabase
        .from("user_app_folders")
        .insert({ name: trimmed, icon: finalIcon, color: input.color ?? null, sort_order: nextOrder })
        .select("id")
        .single();
      if (error) throw error;
      return data?.id as string;
    },
    onSuccess: invalidate,
  });

  const renameFolder = useMutation({
    mutationFn: async (input: { id: string; name: string }) => {
      const { error } = await supabase
        .from("user_app_folders")
        .update({ name: input.name.trim() })
        .eq("id", input.id);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const updateFolder = useMutation({
    mutationFn: async (input: { id: string; icon?: string | null; color?: string | null }) => {
      const patch: Record<string, unknown> = {};
      if (input.icon !== undefined) patch.icon = input.icon;
      if (input.color !== undefined) patch.color = input.color;
      if (Object.keys(patch).length === 0) return;
      const { error } = await supabase
        .from("user_app_folders")
        .update(patch)
        .eq("id", input.id);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const deleteFolder = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("user_app_folders").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const reorderFolders = useMutation({
    mutationFn: async (orderedIds: string[]) => {
      // Update each folder's sort_order to its new index. Sequential because
      // Supabase JS doesn't have a bulk-update-by-id-with-different-values primitive.
      await Promise.all(
        orderedIds.map((id, idx) =>
          supabase.from("user_app_folders").update({ sort_order: idx }).eq("id", id)
        )
      );
    },
    // Optimistic update — reorder the cache immediately so the UI doesn't
    // snap back while the server roundtrip is in flight.
    onMutate: async (orderedIds) => {
      await qc.cancelQueries({ queryKey: QUERY_KEY });
      const previous = qc.getQueryData<AppFolder[]>(QUERY_KEY);
      if (previous) {
        const orderIdx = new Map(orderedIds.map((id, i) => [id, i]));
        const next = [...previous]
          .sort((a, b) => (orderIdx.get(a.id) ?? 999) - (orderIdx.get(b.id) ?? 999))
          .map((f, i) => ({ ...f, sort_order: i }));
        qc.setQueryData<AppFolder[]>(QUERY_KEY, next);
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(QUERY_KEY, ctx.previous);
    },
    onSettled: invalidate,
  });

  const toggleMembership = useMutation({
    mutationFn: async (input: { folderId: string; kind: FolderItemKind; ref: string }) => {
      const { data: existing } = await supabase
        .from("user_app_folder_memberships")
        .select("id")
        .eq("folder_id", input.folderId)
        .eq("item_kind", input.kind)
        .in("item_ref", refAliases(input.kind, input.ref))
        .limit(1)
        .maybeSingle();
      if (existing?.id) {
        const { error } = await supabase
          .from("user_app_folder_memberships")
          .delete()
          .eq("id", existing.id);
        if (error) throw error;
        return { added: false };
      }
      const { error } = await supabase
        .from("user_app_folder_memberships")
        .insert({ folder_id: input.folderId, item_kind: input.kind, item_ref: input.ref });
      if (error) throw error;
      return { added: true };
    },
    onSuccess: invalidate,
  });

  const setItemMemberships = useMutation({
    mutationFn: async (input: { kind: FolderItemKind; ref: string; folderIds: string[] }) => {
      const { data: current } = await supabase
        .from("user_app_folder_memberships")
        .select("id, folder_id")
        .eq("item_kind", input.kind)
        .in("item_ref", refAliases(input.kind, input.ref));
      const currentMap = new Map<string, string>();
      for (const r of (current ?? []) as { id: string; folder_id: string }[]) {
        currentMap.set(r.folder_id, r.id);
      }
      const desired = new Set(input.folderIds);
      const toRemove = [...currentMap.entries()]
        .filter(([fid]) => !desired.has(fid))
        .map(([, id]) => id);
      const toAdd = input.folderIds.filter((fid) => !currentMap.has(fid));
      if (toRemove.length > 0) {
        const { error } = await supabase
          .from("user_app_folder_memberships")
          .delete()
          .in("id", toRemove);
        if (error) throw error;
      }
      if (toAdd.length > 0) {
        const { error } = await supabase
          .from("user_app_folder_memberships")
          .insert(toAdd.map((fid) => ({ folder_id: fid, item_kind: input.kind, item_ref: input.ref })));
        if (error) throw error;
      }
    },
    onSuccess: invalidate,
  });

  return {
    createFolder,
    renameFolder,
    updateFolder,
    deleteFolder,
    reorderFolders,
    toggleMembership,
    setItemMemberships,
  };
}

/** Build a Set of "kind:ref" keys for fast membership lookup. */
export function buildMemberKeySet(folder: AppFolder): Set<string> {
  return new Set(folder.members.map((m) => `${m.kind}:${m.ref}`));
}

/** Folder ID list that contain the given item. */
export function foldersContainingItem(
  folders: AppFolder[],
  kind: FolderItemKind,
  ref: string,
): string[] {
  const out: string[] = [];
  for (const f of folders) {
    if (f.members.some((m) => m.kind === kind && m.ref === ref)) out.push(f.id);
  }
  return out;
}
